import React from 'react';
import PropTypes from 'prop-types';
import { LuMapPin, LuSearch, LuUsersRound, LuX } from 'react-icons/lu';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { GLASS_FIELD } from '../../../components/ui/glass';
import useAccessText from '../accessText';
import { isActiveEmployee } from '../accessUtils';
import { PassiveTag, PersonAvatar } from './accessBits';

export const CARD_SURFACE = 'tw-flex tw-min-h-0 tw-min-w-0 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * The employees (left column): search, Tümü / Çevrimiçi / Ek yetkili and the
 * list with online state, the page they are on and how many pages they can
 * open. On wide screens it scrolls inside its card.
 */
const EmployeeList = ({
  loading, employees, total, onlineUsers, onlineActivity, accessibleCount,
  search, onSearch, filter, onFilter, onlineCount, grantedCount, selectedId, onSelect, className = '',
}) => {
  const { t, label, placeLabel } = useAccessText();

  return (
    <section aria-label={label('list.label')} className={`${CARD_SURFACE} ${className}`}>
      <div className="tw-space-y-3 tw-border-b tw-border-slate-200/70 tw-p-3 sm:tw-p-4 dark:tw-border-white/10">
        <label className="tw-relative tw-block">
          <span className="tw-sr-only">{label('list.search')}</span>
          <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={label('list.search')}
            className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`}
          />
          {search && (
            <button type="button" onClick={() => onSearch('')} aria-label={t('common.clearSearch')} className="tw-absolute tw-right-2 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white">
              <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
            </button>
          )}
        </label>
        <SegmentedControl
          size="sm"
          layoutId="page-access-list-filter"
          label={label('list.filterLabel')}
          value={filter}
          onChange={onFilter}
          className="tw-w-full"
          options={[
            { value: 'all', label: label('list.all') },
            { value: 'online', label: `${label('list.online')} · ${onlineCount}` },
            { value: 'granted', label: `${label('list.granted')} · ${grantedCount}`, title: label('list.grantedHint') },
          ]}
        />
        <p className="tw-px-1 tw-text-xs tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">
          {employees.length === total ? label('list.count', { count: total }) : `${employees.length} / ${label('list.count', { count: total })}`}
        </p>
      </div>

      <ul className="tw-min-h-0 tw-flex-1 tw-space-y-1 tw-overflow-y-auto tw-p-2 glass-scrollbar">
        {loading && (
          <li className="tw-space-y-2 tw-p-2" aria-label={label('list.loading')}>
            {[0, 1, 2, 3, 4].map((index) => <span key={index} className="tw-block tw-h-14 tw-animate-pulse tw-rounded-2xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
          </li>
        )}
        {!loading && employees.length === 0 && (
          <li className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            <LuUsersRound aria-hidden="true" className="tw-h-6 tw-w-6 tw-text-slate-400" />
            {label('list.empty')}
          </li>
        )}
        {!loading && employees.map((emp) => {
          const online = onlineUsers.has(emp.id);
          const passive = !isActiveEmployee(emp);
          const selected = selectedId === emp.id;
          const place = online ? placeLabel(onlineActivity[emp.id]?.page) : null;
          const duration = online ? onlineActivity[emp.id]?.duration : null;
          return (
            <li key={emp.id}>
              <button
                type="button"
                onClick={() => onSelect(emp)}
                aria-current={selected ? 'true' : undefined}
                className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                  selected
                    ? 'tw-border-teal-500/40 tw-bg-teal-500/10'
                    : 'tw-border-transparent hover:tw-bg-slate-900/[0.035] dark:hover:tw-bg-white/[0.05]'
                } ${passive ? 'tw-opacity-75' : ''}`}
              >
                <PersonAvatar employee={emp} online={online} passive={passive} />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
                    <span className={`tw-truncate tw-text-sm tw-font-semibold ${selected ? 'tw-text-teal-900 dark:tw-text-teal-100' : 'tw-text-slate-900 dark:tw-text-white'}`}>
                      {emp.eName} {emp.eSurname}
                    </span>
                    {passive && <PassiveTag />}
                  </span>
                  <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{emp.eMail || '—'}</span>
                  {place && (
                    <span className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-emerald-700 dark:tw-text-emerald-300">
                      <LuMapPin aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                      <span className="tw-truncate">{place.title}</span>
                      {duration && <span className="tw-shrink-0 tw-text-emerald-600/70 dark:tw-text-emerald-300/70">· {duration}</span>}
                    </span>
                  )}
                </span>
                <span title={label('list.accessTitle')} className="tw-grid tw-h-7 tw-min-w-[2rem] tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2 tw-text-xs tw-font-bold tw-tabular-nums tw-text-slate-600 dark:tw-bg-white/[0.08] dark:tw-text-slate-300">
                  {accessibleCount(emp)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

EmployeeList.propTypes = {
  loading: PropTypes.bool,
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
  onlineUsers: PropTypes.instanceOf(Set).isRequired,
  onlineActivity: PropTypes.object.isRequired,
  accessibleCount: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  filter: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  onlineCount: PropTypes.number.isRequired,
  grantedCount: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default EmployeeList;
